import React from 'react';
import { Route, NavLink, useNavigate, useParams, Routes, Navigate } from 'react-router-dom';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

import Button from 'carbon-react/lib/components/button';

import {
  getReportingUrl,
  getReportingDocumentLibraryAwaitingReviewUrl,
  getReportingDocumentLibraryExportedPDFsUrl,
  getReportingDocumentLibraryScheduledReportsUrl,
  getReportingDocumentLibrarySentPDFsUrl,
  ROUTES
} from '@futrli/shared-services';

import { AwaitingReview } from './awaiting-review/awaiting-review';
import { ExportedPdfs } from './exported-pdfs/exported-pdfs';
import { ScheduledReports } from './scheduled-reports/scheduled-reports';
import { SentPdfs } from './sent-pdfs/sent-pdfs';
import { useNavHeight } from '../../../hooks/useNavHeight';

import * as styles from './document-library.scss';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';

export const DocumentLibrary = () => {
  const navigate = useNavigate();
  const { isConsolidation } = usePredictionsInfoContext();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisatioId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisatioId || '';
  const { navHeight } = useNavHeight();

  const onExitClick = () => {
    navigate(getReportingUrl(workspaceId, organisationId, isConsolidation));
  };

  const routes = [
    {
      path: ROUTES.EXPORTED_PDFS,
      url: getReportingDocumentLibraryExportedPDFsUrl(workspaceId, organisationId, isConsolidation),
      component: ExportedPdfs,
      label: <FormattedMessage id="reporting.exported_pdfs" defaultMessage="Exported PDFs" />
    },
    {
      path: ROUTES.SCHEDULED_REPORTS,
      url: getReportingDocumentLibraryScheduledReportsUrl(workspaceId, organisationId, isConsolidation),
      component: ScheduledReports,
      label: <FormattedMessage id="reporting.scheduled_reports" defaultMessage="Scheduled reports" />
    },
    {
      path: ROUTES.AWAITING_REVIEW,
      url: getReportingDocumentLibraryAwaitingReviewUrl(workspaceId, organisationId, isConsolidation),
      component: AwaitingReview,
      label: <FormattedMessage id="reporting.awaiting_review" defaultMessage="Awaiting review" />
    },
    {
      path: ROUTES.SENT_PDFS,
      url: getReportingDocumentLibrarySentPDFsUrl(workspaceId, organisationId, isConsolidation),
      component: SentPdfs,
      label: <FormattedMessage id="reporting.sent_pdfs" defaultMessage="Sent PDFs" />
    }
  ];

  return (
    <div>
      <nav
        className={clsx(styles.navigation, styles.pageWidthWrapper)}
        // eslint-disable-next-line no-magic-numbers
        style={{ top: navHeight }}
      >
        <div className={styles.navigationLinks}>
          <ul data-testid="documentLibraryNav">
            {routes.map(route => (
              <li key={route.path}>
                <NavLink
                  to={route.url}
                  className={({ isActive }) =>
                    clsx(styles.tabNavLink, 'uiTypeOtherAction', {
                      [styles.isActive]: isActive
                    })
                  }
                >
                  {route.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
        <Button onClick={onExitClick} buttonType="primary" size="small">
          <FormattedMessage id="common.exit" defaultMessage="Exit" />
        </Button>
      </nav>

      <div className={styles.content} data-testid="documentLibraryContent">
        <Routes>
          {routes.map(({ path, component: Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
          <Route
            path="*"
            element={
              <Navigate to={getReportingDocumentLibraryExportedPDFsUrl(workspaceId, organisationId, isConsolidation)} />
            }
          />
        </Routes>
      </div>
    </div>
  );
};
